import { ApiError } from "@/lib/api";

const BY_STATUS: Record<number, string> = {
  400: "Requête invalide. Vérifiez les champs saisis.",
  401: "Session expirée. Veuillez vous reconnecter.",
  403: "Vous n'avez pas l'autorisation d'effectuer cette action.",
  404: "Élément introuvable.",
  409: "Cet élément existe déjà.",
  422: "Données non valides.",
  429: "Trop de requêtes. Réessayez dans un instant.",
};

// motif technique (insensible casse) → message convivial
const BY_PATTERN: [RegExp, string][] = [
  [/dioceseId requis|diocèse.*requis/i, "Sélectionnez d'abord un diocèse."],
  [/aucun diocèse rattaché/i, "Aucun diocèse n'est rattaché à votre compte."],
  [/hors de votre (périmètre|scope)|hors de votre diocèse/i, "Cet élément n'est pas dans votre diocèse."],
  [/rôle privilégié|privilege/i, "Vous ne pouvez pas attribuer ce rôle."],
  // Le NUMÉRO avant l'e-mail : « Un compte avec ce numéro de téléphone existe
  // déjà » ne contient ni « email » ni « adresse » et retombait donc sur le
  // fourre-tout « Cet élément existe déjà. » — un parent bloqué par son numéro
  // n'avait aucun moyen de le deviner.
  [/(num[ée]ro|t[ée]l[ée]phone).*(utilis|exist)/i, "Ce numéro est déjà rattaché à un compte."],
  [/(e-?mail|adresse).*(utilis|exist)|email .*(utilis|exist)/i, "Cet e-mail est déjà utilisé."],
  [/existe déjà|déjà utilis/i, "Cet élément existe déjà."],
  [/mot de passe actuel incorrect/i, "Mot de passe actuel incorrect."],
  [/profil non autorisé/i, "Ce profil n'est pas autorisé sur cet écran."],
];

// Messages métier compta (PaiementService) déjà clairs en français : on les
// laisse passer TELS QUELS (l'économe doit voir « aucun exercice comptable
// ouvert… ») plutôt que de les masquer derrière « Requête invalide ». Lot 4.
const COMPTA_PASSTHROUGH = /exercice comptable|plan comptable|journal/i;

// Garde « année active » (Lot D) : l'écriture sur une année non-active renvoie
// un 400 avec un message métier déjà clair (« Écriture autorisée uniquement sur
// l'année active. Sélectionnez l'année en cours. ») — on le laisse passer TEL QUEL
// plutôt que de le masquer derrière « Requête invalide ».
const ANNEE_ACTIVE_PASSTHROUGH = /année active|année en cours/i;

// Suppression d'un motif déjà payé (lot E) : le 409 porte un message métier
// précis (« Ce motif a déjà reçu N paiement(s) : désactivez-le… ») — le
// mapper générique 409 (« Cet élément existe déjà. ») serait un contresens.
const SUPPRESSION_MOTIF_PASSTHROUGH = /désactivez-le au lieu de le supprimer/i;

// Reste à régler (22/10/2026) : les refus du serveur sur le montant d'un
// paiement (en ligne ou au guichet) disent déjà, en français, le reste exact et
// la tranche en cause. Les masquer derrière « Cet élément existe déjà » (409)
// ou « Requête invalide » (400) laisserait le parent sans le bon montant.
const RESTE_PASSTHROUGH =
  /supérieur au reste|exactement le reste|déjà soldée?|Tarif non défini|Tranche inconnue|échéancier d'une autre classe/i;

/**
 * Quarkus renvoie les échecs de `@Valid` sous la forme
 * `{"title":"Constraint Violation","violations":[{field,message}]}` : ni
 * `message` ni `error` n'y figurent, si bien qu'un 400 se réduisait à
 * « Requête invalide. Vérifiez les champs saisis. » sans jamais dire QUEL champ.
 * Les messages du serveur sont déjà rédigés en français — on les remonte.
 */
export function violationMessage(err: unknown): string | null {
  // Lecture STRUCTURELLE du payload (et non `err instanceof ApiError`) : cette
  // fonction est appelée en tête de friendlyError, y compris dans les suites qui
  // remplacent `@/lib/api` par un mock partiel où `ApiError` n'existe pas.
  const payload = (err as { payload?: unknown } | null | undefined)?.payload;
  if (!payload || typeof payload !== "object") return null;
  const violations = (payload as { violations?: unknown }).violations;
  if (!Array.isArray(violations)) return null;

  const messages: string[] = [];
  for (const violation of violations) {
    const message =
      violation && typeof violation === "object"
        ? (violation as { message?: unknown }).message
        : null;
    const trimmed = typeof message === "string" ? message.trim() : "";
    if (trimmed && !messages.includes(trimmed)) messages.push(trimmed);
  }
  return messages.length > 0 ? messages.join(" ") : null;
}

/**
 * Message dicté par le CODE de conflit du serveur (409). L'adresse et le
 * numéro sont tous deux uniques : sans cette distinction, un parent bloqué par
 * son numéro lit « Cet e-mail est déjà utilisé » et change d'adresse en vain.
 * Lecture structurelle du payload — voir {@link violationMessage}.
 */
export function conflictCode(err: unknown): string | null {
  const payload = (err as { payload?: unknown } | null | undefined)?.payload;
  if (!payload || typeof payload !== "object") return null;
  const code = (payload as { code?: unknown }).code;
  if (code === "PHONE_ALREADY_EXISTS") return "Ce numéro est déjà rattaché à un compte.";
  if (code === "EMAIL_ALREADY_EXISTS") return "Cet e-mail est déjà utilisé.";
  return null;
}

/** Traduit n'importe quelle erreur en message FR convivial pour un toast. */
export function friendlyError(err: unknown): string {
  const raw = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  if (COMPTA_PASSTHROUGH.test(raw)) return raw;
  if (ANNEE_ACTIVE_PASSTHROUGH.test(raw)) return raw;
  if (SUPPRESSION_MOTIF_PASSTHROUGH.test(raw)) return raw;
  if (RESTE_PASSTHROUGH.test(raw)) return raw;
  const violations = violationMessage(err);
  if (violations) return violations;
  const conflit = conflictCode(err);
  if (conflit) return conflit;
  for (const [re, msg] of BY_PATTERN) if (re.test(raw)) return msg;
  if (err instanceof ApiError) {
    if (err.status >= 500) return "Erreur serveur. Réessayez plus tard.";
    const byStatus = BY_STATUS[err.status];
    if (byStatus) return byStatus;
  }
  // message backend lisible (pas de stacktrace/HTML) sinon générique
  if (raw && raw.length < 200 && !/<|\bException\b|stack/i.test(raw)) return raw;
  return "Une erreur est survenue. Réessayez.";
}
